import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
    Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../components/ui/sheet';
import {
    Activity,
    AlertTriangle,
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    CheckCircle2,
    RefreshCw,
    Search,
    Zap,
    ShieldAlert,
    Radio,
    ShieldOff,
    Info,
} from 'lucide-react';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    CartesianGrid,
} from 'recharts';
import { tapHealthAPI } from '../lib/api';
import { fmtLocalDateTime } from '../utils/localTime';
import { HelpButton } from '../components/docs/HelpButton';
import { Timestamp } from '../components/common/Timestamp';

/**
 * P_TAP_HEALTH_MONITOR_1.0 — Super-admin observability page. Never
 * mutates any data. Detects silent tap-analytics failures BEFORE
 * customers report them.
 */

const numberFmt = (n) => (n ?? 0).toLocaleString();

/**
 * P_TAP_PROTECTION_1.0 — Render the Tap Protection view returned by
 * `/api/admin/tap-health/card-diagnostics/{card_id}` verbatim. Every
 * rendered value reflects what the runtime is actually enforcing.
 */
const TapProtectionSummary = ({ view, t }) => {
    if (!view) return <div className="text-slate-500">—</div>;
    const moduleLabelKey = (id) => {
        if (id === 'duplicate') return 'tapProtection.duplicate.title';
        if (id === 'dwell') return 'tapProtection.dwell.title';
        return null;
    };
    const notApplicableReasonKey = (id) => {
        if (id === 'dwell') return 'tapProtection.dwell.notApplicable';
        return null;
    };
    return (
        <div className="space-y-2" data-testid="tap-protection-summary">
            <div className="text-sm">
                <span className="text-slate-500">
                    {t('tapProtection.title')}:{' '}
                </span>
                <span
                    className={view.enabled ? 'text-emerald-700 font-medium' : 'text-slate-500'}
                    data-testid="tap-protection-master-status"
                >
                    {view.enabled
                        ? t('tapProtection.status.enabled')
                        : t('tapProtection.status.disabled')}
                </span>
            </div>
            {view.modules?.map((m) => {
                const labelKey = moduleLabelKey(m.id);
                const label = labelKey ? t(labelKey) : m.label;
                const reasonKey = notApplicableReasonKey(m.id);
                const reason = reasonKey ? t(reasonKey) : m.not_applicable_reason;
                const line = !view.enabled
                    ? t('tapProtection.status.disabledMasterOff')
                    : !m.enabled
                        ? t('tapProtection.status.disabled')
                        : !m.applies_to_this_card
                            ? t('tapProtection.status.notApplicable')
                            : m.id === 'duplicate'
                                ? t('tapProtection.status.enabledWindow', {
                                    seconds: m.params?.window_seconds ?? '—',
                                })
                                : m.id === 'dwell'
                                    ? t('tapProtection.status.enabledThreshold', {
                                        seconds: m.params?.threshold_seconds ?? '—',
                                    })
                                    : t('tapProtection.status.enabled');
                return (
                    <div
                        key={m.id}
                        className="text-sm"
                        data-testid={`tap-protection-module-${m.id}`}
                    >
                        <div className="text-slate-700">{label}:</div>
                        <div className="pl-2 text-slate-600">{line}</div>
                        {!m.applies_to_this_card && reason ? (
                            <div className="pl-2 text-xs text-amber-700 mt-0.5">
                                {t('tapProtection.status.reasonPrefix', { reason })}
                            </div>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
};

/** Human-readable label for a suppression log entry. i18n-first. */
const humanSuppression = (s, t) => {
    if (s.action === 'duplicate_suppressed') return t('tapProtection.suppression.duplicateTap');
    if (s.action === 'dwell_time_not_met') return t('tapProtection.suppression.dwellNotMet');
    // Unknown action — fall back to backend label if provided
    return s.label || s.action || t('tapProtection.suppression.rejected');
};

/** Map backend verdict slug -> camelCase i18n key. */
const verdictKey = (v) => {
    const map = {
        'healthy': 'healthy',
        'silent': 'silent',
        'silent-with-suppressions': 'silentWithSuppressions',
        'write-failures': 'writeFailures',
    };
    return map[v] || 'healthy';
};

/** Mask the last octet of an IPv4 address (privacy-preserving audit).
 *  IPv6 shows the first three groups only. Falsy input → em-dash. */
const maskIp = (ip) => {
    if (!ip || typeof ip !== 'string') return '—';
    if (ip.includes(':')) {
        const parts = ip.split(':').filter(Boolean);
        return parts.slice(0, 3).join(':') + '::…';
    }
    const parts = ip.split('.');
    if (parts.length !== 4) return ip;
    return `${parts[0]}.${parts[1]}.${parts[2]}.×`;
};

/** Map backend-provided alert English label / id → localized string.
 *  Falls back to the label from the API for any unknown alert. */
const alertLabel = (a, t) => {
    const id = a.id || '';
    const key = ({
        'suppression_spike_(any_reason)': 'suppressionSpikeAny',
        'dwell-time_suppression_spike':   'dwellTimeSuppressionSpike',
        'duplicate_suppression_spike':    'duplicateSuppressionSpike',
        'tap_write_failures':             'tapWriteFailures',
    })[id];
    if (!key) return a.label || id;
    return t(`tapHealthMonitor.alerts.labels.${key}`, a.label || id);
};

/** Localized reason string for a unified event row. */
const reasonLabel = (ev, t) => {
    const k = ev.reason_key;
    if (k === 'new_tap') return t('tapHealthMonitor.diagnostic.reason.new_tap');
    if (k === 'new_session') return t('tapHealthMonitor.diagnostic.reason.new_session');
    if (k === 'duplicate_suppressed') {
        const gap = ev.gap_seconds;
        if (gap != null) {
            return t('tapHealthMonitor.diagnostic.reason.duplicate_suppressed', {
                seconds: gap < 1 ? `${(gap * 1000).toFixed(0)} ms` : `${gap.toFixed(1)} s`,
            });
        }
        return t('tapHealthMonitor.diagnostic.reason.duplicate_suppressed_bare');
    }
    if (k === 'dwell_time_not_met') {
        return t('tapHealthMonitor.diagnostic.reason.dwell_time_not_met', {
            dwell: ev.dwell_seconds ?? '?',
            threshold: ev.dwell_threshold ?? '?',
        });
    }
    return t('tapHealthMonitor.diagnostic.reason.rejected');
};

/** Slide-in Raw Event drawer. */
const RawEventDrawer = ({ open, onOpenChange, event, t }) => {
    if (!event) return null;
    const na = t('tapHealthMonitor.diagnostic.raw.notAvailable');
    const rows = [
        ['Timestamp', event.timestamp],
        [t('tapHealthMonitor.diagnostic.raw.decision'),
            event.kind === 'accepted'
                ? `✅ ${t('tapHealthMonitor.diagnostic.result.counted')}`
                : `🚫 ${t('tapHealthMonitor.diagnostic.result.suppressed')}`],
        [t('tapHealthMonitor.diagnostic.raw.suppressionReason'), reasonLabel(event, t)],
        [t('tapHealthMonitor.diagnostic.raw.duplicateWindow'),
            event.duplicate_window ? `${event.duplicate_window} s` : na],
        [t('tapHealthMonitor.diagnostic.cols.cardId'), event.card_id],
        [t('tapHealthMonitor.diagnostic.cols.employee'), event.employee_name || na],
        [t('tapHealthMonitor.diagnostic.cols.platform'), event.platform || na],
        [t('tapHealthMonitor.diagnostic.raw.device'), event.user_agent || na],
        [t('tapHealthMonitor.diagnostic.cols.ip'), maskIp(event.ip)],
        [t('tapHealthMonitor.diagnostic.raw.session'), event.fingerprint || na],
        [t('tapHealthMonitor.diagnostic.raw.organization'), event.organization_id || na],
        [t('tapHealthMonitor.diagnostic.raw.location'), event.location_id || na],
    ];
    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="raw-event-drawer">
                <SheetHeader>
                    <SheetTitle>{t('tapHealthMonitor.diagnostic.raw.title')}</SheetTitle>
                </SheetHeader>
                <dl className="mt-4 text-sm space-y-2">
                    {rows.map(([label, value]) => (
                        <div
                            key={label}
                            className="grid grid-cols-1 sm:grid-cols-3 gap-x-2 gap-y-0.5 border-b border-slate-100 py-1.5"
                        >
                            <dt className="sm:col-span-1 text-slate-500 text-xs uppercase tracking-wide">{label}</dt>
                            <dd className="sm:col-span-2 font-mono text-slate-800 break-all">{String(value ?? na)}</dd>
                        </div>
                    ))}
                </dl>
            </SheetContent>
        </Sheet>
    );
};

/**
 * P_ANALYTICS_EVENT_STREAM_1.0 — Unified event stream (accepted +
 * suppressed) with Result + Reason columns, toggleable diagnostic
 * columns, and a per-row "View raw event" drawer.
 */
const OPTIONAL_COLS = [
    { id: 'card_id',    labelKey: 'tapHealthMonitor.diagnostic.cols.cardId',    defaultOn: false },
    { id: 'employee',   labelKey: 'tapHealthMonitor.diagnostic.cols.employee',  defaultOn: false },
    { id: 'platform',   labelKey: 'tapHealthMonitor.diagnostic.cols.platform',  defaultOn: false },
    { id: 'device',     labelKey: 'tapHealthMonitor.diagnostic.cols.device',    defaultOn: false },
    { id: 'ip',         labelKey: 'tapHealthMonitor.diagnostic.cols.ip',        defaultOn: false },
    { id: 'session_id', labelKey: 'tapHealthMonitor.diagnostic.cols.sessionId', defaultOn: false },
];

const EventStreamTable = ({ events, t }) => {
    const [visibleCols, setVisibleCols] = useState(
        () => new Set(OPTIONAL_COLS.filter((c) => c.defaultOn).map((c) => c.id)),
    );
    const [rawEvent, setRawEvent] = useState(null);
    // Sort state. `dir` is 'desc' (newest first) by default for timestamps
    // so operators immediately see the latest activity. Toggling the same
    // column cycles asc → desc → asc.
    const [sortKey, setSortKey] = useState('timestamp');
    const [sortDir, setSortDir] = useState('desc');
    const toggle = (id) => {
        const next = new Set(visibleCols);
        if (next.has(id)) next.delete(id); else next.add(id);
        setVisibleCols(next);
    };
    const requestSort = (key) => {
        if (sortKey === key) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            // For string-based columns start ascending; timestamps default desc.
            setSortDir(key === 'timestamp' ? 'desc' : 'asc');
        }
    };
    // Derived, stable-sorted rows. Original array preserved.
    const sortedEvents = useMemo(() => {
        const rows = [...events];
        const cmp = (a, b) => {
            const dir = sortDir === 'asc' ? 1 : -1;
            let av;
            let bv;
            switch (sortKey) {
                case 'result':
                    av = a.kind || '';
                    bv = b.kind || '';
                    break;
                case 'reason':
                    av = reasonLabel(a, t) || '';
                    bv = reasonLabel(b, t) || '';
                    break;
                case 'card_id':
                    av = a.card_id || '';
                    bv = b.card_id || '';
                    break;
                case 'employee':
                    av = a.employee_name || '';
                    bv = b.employee_name || '';
                    break;
                case 'platform':
                    av = a.platform || '';
                    bv = b.platform || '';
                    break;
                case 'device':
                    av = a.user_agent || '';
                    bv = b.user_agent || '';
                    break;
                case 'ip':
                    av = a.ip || '';
                    bv = b.ip || '';
                    break;
                case 'session_id':
                    av = a.fingerprint || '';
                    bv = b.fingerprint || '';
                    break;
                case 'timestamp':
                default:
                    av = a.timestamp ? new Date(a.timestamp).getTime() : 0;
                    bv = b.timestamp ? new Date(b.timestamp).getTime() : 0;
                    break;
            }
            if (typeof av === 'number' && typeof bv === 'number') {
                return (av - bv) * dir;
            }
            return String(av).localeCompare(String(bv), undefined, {
                sensitivity: 'base',
                numeric: true,
            }) * dir;
        };
        rows.sort(cmp);
        return rows;
    }, [events, sortKey, sortDir, t]);
    const renderSortIcon = (colKey) => {
        if (sortKey !== colKey) {
            return <ArrowUpDown className="inline h-3 w-3 ml-1 text-slate-300" />;
        }
        return sortDir === 'asc'
            ? <ArrowUp className="inline h-3 w-3 ml-1 text-slate-600" />
            : <ArrowDown className="inline h-3 w-3 ml-1 text-slate-600" />;
    };
    const renderSortableTh = (colKey, label, className = '') => (
        <th
            key={colKey}
            className={`px-2 py-1.5 select-none cursor-pointer hover:bg-slate-100 transition-colors ${className}`}
            onClick={() => requestSort(colKey)}
            aria-sort={sortKey === colKey ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
            data-testid={`event-stream-sort-${colKey}`}
        >
            <span className="inline-flex items-center whitespace-nowrap">
                {label}
                {renderSortIcon(colKey)}
            </span>
        </th>
    );
    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                <div className="text-slate-500 text-xs uppercase tracking-wide">
                    {t('tapHealthMonitor.diagnostic.eventStream')}
                </div>
                <div
                    className="flex flex-wrap gap-1 -mx-0.5 px-0.5 overflow-x-auto sm:overflow-visible"
                    data-testid="suppressions-col-toggles"
                >
                    {OPTIONAL_COLS.map((c) => (
                        <button
                            key={c.id}
                            type="button"
                            onClick={() => toggle(c.id)}
                            className={`text-[10px] uppercase px-2 py-0.5 rounded border transition-colors whitespace-nowrap ${
                                visibleCols.has(c.id)
                                    ? 'bg-emerald-100 border-emerald-300 text-emerald-800'
                                    : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100'
                            }`}
                            data-testid={`suppressions-col-toggle-${c.id}`}
                        >
                            {t(c.labelKey)}
                        </button>
                    ))}
                </div>
            </div>
            <div className="overflow-x-auto max-h-80 overflow-y-auto border rounded">
                <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-600 text-left sticky top-0">
                        <tr>
                            {renderSortableTh('timestamp', t('tapHealthMonitor.diagnostic.cols.timestamp'))}
                            {renderSortableTh('result', t('tapHealthMonitor.diagnostic.cols.result'))}
                            {renderSortableTh('reason', t('tapHealthMonitor.diagnostic.cols.reason'))}
                            {OPTIONAL_COLS.map((c) =>
                                visibleCols.has(c.id)
                                    ? renderSortableTh(c.id, t(c.labelKey))
                                    : null,
                            )}
                            <th className="px-2 py-1.5 w-8" aria-label={t('tapHealthMonitor.diagnostic.viewRaw')}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedEvents.map((ev) => (
                            <tr
                                key={ev.id}
                                className={`border-t ${ev.kind === 'suppressed' ? 'bg-rose-50/40' : ''}`}
                                data-testid={`event-row-${ev.id}`}
                            >
                                <td className="px-2 py-1.5 whitespace-nowrap">
                                    <Timestamp value={ev.timestamp} testid={`ts-${ev.id}`} />
                                </td>
                                <td className="px-2 py-1.5 whitespace-nowrap">
                                    {ev.kind === 'accepted' ? (
                                        <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                                            <CheckCircle2 className="h-3.5 w-3.5" />
                                            {t('tapHealthMonitor.diagnostic.result.counted')}
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 text-rose-700 font-medium">
                                            <ShieldOff className="h-3.5 w-3.5" />
                                            {t('tapHealthMonitor.diagnostic.result.suppressed')}
                                        </span>
                                    )}
                                </td>
                                <td className="px-2 py-1.5">{reasonLabel(ev, t)}</td>
                                {visibleCols.has('card_id') ? (
                                    <td className="px-2 py-1.5 font-mono whitespace-nowrap">{ev.card_id || '—'}</td>
                                ) : null}
                                {visibleCols.has('employee') ? (
                                    <td className="px-2 py-1.5 whitespace-nowrap">{ev.employee_name || '—'}</td>
                                ) : null}
                                {visibleCols.has('platform') ? (
                                    <td className="px-2 py-1.5 whitespace-nowrap">{ev.platform || '—'}</td>
                                ) : null}
                                {visibleCols.has('device') ? (
                                    <td className="px-2 py-1.5 truncate max-w-[10rem]" title={ev.user_agent || ''}>
                                        {ev.user_agent || '—'}
                                    </td>
                                ) : null}
                                {visibleCols.has('ip') ? (
                                    <td className="px-2 py-1.5 font-mono whitespace-nowrap">{maskIp(ev.ip)}</td>
                                ) : null}
                                {visibleCols.has('session_id') ? (
                                    <td className="px-2 py-1.5 font-mono truncate max-w-[7rem]" title={ev.fingerprint || ''}>
                                        {ev.fingerprint || '—'}
                                    </td>
                                ) : null}
                                <td className="px-2 py-1.5">
                                    <button
                                        type="button"
                                        onClick={() => setRawEvent(ev)}
                                        title={t('tapHealthMonitor.diagnostic.viewRaw')}
                                        aria-label={t('tapHealthMonitor.diagnostic.viewRaw')}
                                        className="text-slate-400 hover:text-slate-700"
                                        data-testid={`raw-event-btn-${ev.id}`}
                                    >
                                        <Info className="h-3.5 w-3.5" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <RawEventDrawer
                open={!!rawEvent}
                onOpenChange={(v) => !v && setRawEvent(null)}
                event={rawEvent}
                t={t}
            />
        </div>
    );
};

/** Summary strip shown atop the per-card diagnostic. */
const CardEventsSummary = ({ summary, t }) => {
    if (!summary) return null;
    const pctSuffix = summary.total_reads
        ? ` (${summary.duplicate_pct}%)`
        : '';
    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="card-events-summary">
            <div className="p-3 rounded bg-slate-50 border">
                <div className="text-[10px] uppercase text-slate-500">
                    {t('tapHealthMonitor.diagnostic.summary.totalReads')}
                </div>
                <div className="text-xl font-semibold text-slate-900 mt-1">
                    {numberFmt(summary.total_reads)}
                </div>
            </div>
            <div className="p-3 rounded bg-emerald-50 border border-emerald-100">
                <div className="text-[10px] uppercase text-emerald-700">
                    {t('tapHealthMonitor.diagnostic.summary.counted')}
                </div>
                <div className="text-xl font-semibold text-emerald-800 mt-1">
                    {numberFmt(summary.counted)}
                </div>
            </div>
            <div className="p-3 rounded bg-rose-50 border border-rose-100">
                <div className="text-[10px] uppercase text-rose-700">
                    {t('tapHealthMonitor.diagnostic.summary.duplicateSuppressed')}
                </div>
                <div className="text-xl font-semibold text-rose-800 mt-1">
                    {numberFmt(summary.duplicate_suppressed)}{pctSuffix}
                </div>
            </div>
            <div className="p-3 rounded bg-slate-50 border">
                <div className="text-[10px] uppercase text-slate-500">
                    {t('tapHealthMonitor.diagnostic.summary.avgGap')}
                </div>
                <div className="text-xl font-semibold text-slate-900 mt-1">
                    {summary.avg_gap_seconds_between_duplicates != null
                        ? `${summary.avg_gap_seconds_between_duplicates.toFixed(1)} s`
                        : '—'}
                </div>
            </div>
        </div>
    );
};

/** Legacy shim — replaced by EventStreamTable but retained so any
 *  external caller of `SuppressionsTable` keeps compiling. */
const SuppressionsTable = ({ rows, t }) => (
    <EventStreamTable
        events={rows.map((r) => ({
            ...r,
            kind: 'suppressed',
            reason_key: r.action,
            gap_seconds: null,
            duplicate_window: r.window_seconds,
        }))}
        t={t}
    />
);

const severityStyles = {
    critical: 'bg-rose-100 text-rose-700 border-rose-200',
    warning: 'bg-amber-100 text-amber-700 border-amber-200',
    info: 'bg-sky-100 text-sky-700 border-sky-200',
};

const StatCard = ({ label, value, hint, testid, tone = 'neutral' }) => {
    const toneClass =
        tone === 'good'
            ? 'text-emerald-700'
            : tone === 'warn'
                ? 'text-amber-700'
                : tone === 'bad'
                    ? 'text-rose-700'
                    : 'text-slate-900';
    return (
        <Card className="p-4" data-testid={testid}>
            <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
            <div className={`text-2xl font-semibold mt-1 ${toneClass}`}>{value}</div>
            {hint ? <div className="text-xs text-slate-500 mt-1">{hint}</div> : null}
        </Card>
    );
};

const TapHealthMonitor = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [summary, setSummary] = useState(null);
    const [throughput, setThroughput] = useState(null);
    const [suppressions, setSuppressions] = useState(null);
    const [split, setSplit] = useState(null);
    const [silent, setSilent] = useState(null);
    const [failures, setFailures] = useState(null);
    const [alerts, setAlerts] = useState(null);
    const [trend, setTrend] = useState(null);

    const [cardQuery, setCardQuery] = useState('');
    const [cardResult, setCardResult] = useState(null);
    const [cardEvents, setCardEvents] = useState(null);
    const [cardError, setCardError] = useState(null);
    const [cardLoading, setCardLoading] = useState(false);

    const reload = async () => {
        setRefreshing(true);
        setError(null);
        try {
            const [s, t, sup, sp, sc, wf, al, tr] = await Promise.all([
                tapHealthAPI.summary(),
                tapHealthAPI.throughput(),
                tapHealthAPI.suppressions(24),
                tapHealthAPI.directVsLanding(24),
                tapHealthAPI.silentCards(7, 50),
                tapHealthAPI.writeFailures(24, 50),
                tapHealthAPI.alerts(),
                tapHealthAPI.trend(30),
            ]);
            setSummary(s.data);
            setThroughput(t.data);
            setSuppressions(sup.data);
            setSplit(sp.data);
            setSilent(sc.data);
            setFailures(wf.data);
            setAlerts(al.data);
            setTrend(tr.data);
        } catch (e) {
            setError(e.response?.data?.detail || e.message || 'load_failed');
        } finally {
            setRefreshing(false);
            setLoading(false);
        }
    };

    useEffect(() => {
        reload();
        // Poll every 60s for near-live health
        const iv = setInterval(reload, 60000);
        return () => clearInterval(iv);
    }, []);

    const runCardDiagnostic = async (e) => {
        e?.preventDefault();
        if (!cardQuery.trim()) return;
        setCardLoading(true);
        setCardError(null);
        setCardResult(null);
        setCardEvents(null);
        try {
            const [diag, ev] = await Promise.all([
                tapHealthAPI.cardDiagnostics(cardQuery.trim()),
                tapHealthAPI.cardEvents(cardQuery.trim(), 100),
            ]);
            setCardResult(diag.data);
            setCardEvents(ev.data);
        } catch (err) {
            setCardError(err.response?.data?.detail || err.message || 'lookup_failed');
        } finally {
            setCardLoading(false);
        }
    };

    const firingAlerts = useMemo(
        () => (alerts?.alerts || []).filter((a) => a.firing),
        [alerts],
    );

    if (loading) {
        return (
            <div className="p-8 text-slate-600" data-testid="tap-health-loading">
                {t('tapHealthMonitor.loading')}
            </div>
        );
    }

    return (
        <div className="p-6 sm:p-8 max-w-7xl mx-auto space-y-6" data-testid="tap-health-page">
            <header className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h1
                        className="text-3xl font-semibold text-slate-900 flex items-center gap-3"
                        data-testid="tap-health-title"
                    >
                        <Radio className="h-7 w-7 text-emerald-600" />
                        {t('tapHealthMonitor.title')}
                        <HelpButton docId="tapProtection" size="md" />
                    </h1>
                    <p className="text-sm text-slate-600 mt-1 max-w-2xl">
                        {t('tapHealthMonitor.subtitle')}
                    </p>
                </div>
                <Button
                    variant="outline"
                    onClick={reload}
                    disabled={refreshing}
                    data-testid="tap-health-refresh-btn"
                >
                    <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                    {t('tapHealthMonitor.refresh')}
                </Button>
            </header>

            {error ? (
                <Card
                    className="p-4 bg-rose-50 border-rose-200 text-rose-700"
                    data-testid="tap-health-error"
                >
                    {t('tapHealthMonitor.loadFailed', { error })}
                </Card>
            ) : null}

            {/* ---------------- Alerts row ---------------- */}
            <section data-testid="tap-health-alerts-section">
                <h2 className="text-sm uppercase tracking-wide text-slate-500 mb-2">
                    {t('tapHealthMonitor.alerts.sectionLabel')}
                </h2>
                {firingAlerts.length === 0 ? (
                    <Card
                        className="p-4 bg-emerald-50 border-emerald-200 text-emerald-800 flex items-center gap-2"
                        data-testid="tap-health-alerts-clear"
                    >
                        <CheckCircle2 className="h-5 w-5" />
                        {t('tapHealthMonitor.alerts.clear')}
                    </Card>
                ) : (
                    <div className="grid gap-2">
                        {firingAlerts.map((a) => (
                            <Card
                                key={a.id}
                                className={`p-4 border ${severityStyles[a.severity] || severityStyles.warning}`}
                                data-testid={`tap-health-alert-${a.id}`}
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex items-start gap-3">
                                        <AlertTriangle className="h-5 w-5 mt-0.5" />
                                        <div>
                                            <div className="font-semibold">{alertLabel(a, t)}</div>
                                            <div className="text-xs mt-1 opacity-80">
                                                {t('tapHealthMonitor.alerts.context', {
                                                    current: numberFmt(a.current_hour),
                                                    baseline: a.baseline_per_hour_7d,
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                    <Badge className={severityStyles[a.severity]}>
                                        {a.severity}
                                    </Badge>
                                </div>
                            </Card>
                        ))}
                    </div>
                )}
                <div className="mt-2 text-xs text-slate-500">
                    {t('tapHealthMonitor.alerts.rule')}
                </div>
            </section>

            {/* ---------------- Throughput row ---------------- */}
            <section>
                <h2 className="text-sm uppercase tracking-wide text-slate-500 mb-2">
                    {t('tapHealthMonitor.throughput.sectionLabel')}
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard
                        label={t('tapHealthMonitor.throughput.last5m')}
                        value={numberFmt(throughput?.last_5m)}
                        testid="throughput-5m"
                    />
                    <StatCard
                        label={t('tapHealthMonitor.throughput.last1h')}
                        value={numberFmt(throughput?.last_1h)}
                        testid="throughput-1h"
                    />
                    <StatCard
                        label={t('tapHealthMonitor.throughput.last24h')}
                        value={numberFmt(throughput?.last_24h)}
                        testid="throughput-24h"
                    />
                    <StatCard
                        label={t('tapHealthMonitor.throughput.avg7d')}
                        value={numberFmt(throughput?.daily_average_7d)}
                        testid="throughput-avg-7d"
                    />
                </div>
            </section>

            {/* ---------------- Suppressions + direct/landing ---------------- */}
            <section className="grid md:grid-cols-2 gap-6">
                <Card className="p-4" data-testid="suppressions-card">
                    <div className="flex items-center gap-2 mb-3">
                        <ShieldAlert className="h-5 w-5 text-amber-600" />
                        <h3 className="font-semibold">{t('tapHealthMonitor.suppressions.cardTitle')}</h3>
                    </div>
                    <div className="text-3xl font-semibold text-slate-900">
                        {numberFmt(suppressions?.total)}
                    </div>
                    <div className="mt-3 space-y-1">
                        {Object.entries(suppressions?.by_reason || {}).length === 0 ? (
                            <div className="text-sm text-slate-500">{t('tapHealthMonitor.suppressions.empty')}</div>
                        ) : (
                            Object.entries(suppressions.by_reason).map(([k, v]) => (
                                <div
                                    key={k}
                                    className="flex justify-between text-sm"
                                    data-testid={`suppression-reason-${k}`}
                                >
                                    <span className="text-slate-700">
                                        {humanSuppression({ action: k }, t)}
                                    </span>
                                    <span className="font-mono">{numberFmt(v)}</span>
                                </div>
                            ))
                        )}
                    </div>
                </Card>

                <Card className="p-4" data-testid="split-card">
                    <div className="flex items-center gap-2 mb-3">
                        <Zap className="h-5 w-5 text-sky-600" />
                        <h3 className="font-semibold">{t('tapHealthMonitor.split.cardTitle')}</h3>
                    </div>
                    <div className="text-3xl font-semibold text-slate-900">
                        {numberFmt(split?.total)}
                    </div>
                    <div className="mt-3 space-y-1">
                        {Object.entries(split?.by_source || {}).length === 0 ? (
                            <div className="text-sm text-slate-500">{t('tapHealthMonitor.split.empty')}</div>
                        ) : (
                            Object.entries(split.by_source).map(([k, v]) => (
                                <div
                                    key={k}
                                    className="flex justify-between text-sm"
                                    data-testid={`split-source-${k}`}
                                >
                                    <span className="text-slate-700">
                                        {t(`tapHealthMonitor.split.source.${k}`, k.replace(/_/g, ' '))}
                                    </span>
                                    <span className="font-mono">{numberFmt(v)}</span>
                                </div>
                            ))
                        )}
                    </div>
                </Card>
            </section>

            {/* ---------------- Trend chart ---------------- */}
            <section>
                <h2 className="text-sm uppercase tracking-wide text-slate-500 mb-2">
                    {t('tapHealthMonitor.trend.sectionLabel')}
                </h2>
                <Card className="p-4" data-testid="trend-card">
                    <div style={{ width: '100%', height: 280 }}>
                        <ResponsiveContainer>
                            <LineChart data={trend?.series || []}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={20} />
                                <YAxis tick={{ fontSize: 11 }} />
                                <Tooltip />
                                <Legend />
                                <Line
                                    type="monotone"
                                    dataKey="recorded_taps"
                                    name={t('tapHealthMonitor.trend.recordedTaps')}
                                    stroke="#10b981"
                                    dot={false}
                                    strokeWidth={2}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="suppressed"
                                    name={t('tapHealthMonitor.trend.suppressed')}
                                    stroke="#f59e0b"
                                    dot={false}
                                    strokeWidth={2}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="write_failures"
                                    name={t('tapHealthMonitor.trend.writeFailures')}
                                    stroke="#ef4444"
                                    dot={false}
                                    strokeWidth={2}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
            </section>

            {/* ---------------- Silent cards ---------------- */}
            <section>
                <h2 className="text-sm uppercase tracking-wide text-slate-500 mb-2">
                    {t('tapHealthMonitor.silent.sectionLabel')}
                </h2>
                <Card className="p-0 overflow-hidden" data-testid="silent-cards-card">
                    {(silent?.silent_cards || []).length === 0 ? (
                        <div className="p-4 text-sm text-slate-500">
                            {t('tapHealthMonitor.silent.empty')}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 text-slate-600 text-left">
                                    <tr>
                                        <th className="px-3 py-2">{t('tapHealthMonitor.silent.columns.card')}</th>
                                        <th className="px-3 py-2">{t('tapHealthMonitor.silent.columns.organization')}</th>
                                        <th className="px-3 py-2">{t('tapHealthMonitor.silent.columns.mode')}</th>
                                        <th className="px-3 py-2 text-right">{t('tapHealthMonitor.silent.columns.suppressed')}</th>
                                        <th className="px-3 py-2">{t('tapHealthMonitor.silent.columns.lastTap')}</th>
                                        <th className="px-3 py-2"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {silent.silent_cards.map((s) => (
                                        <tr
                                            key={s.card_id}
                                            className="border-t"
                                            data-testid={`silent-row-${s.card_id}`}
                                        >
                                            <td className="px-3 py-2 font-mono">{s.card_id}</td>
                                            <td className="px-3 py-2">{s.organization_name || '—'}</td>
                                            <td className="px-3 py-2">
                                                {s.is_direct_tap
                                                    ? t('tapHealthMonitor.silent.modeDirectTap')
                                                    : s.interaction_mode === 'review_shield'
                                                        ? t('tapHealthMonitor.silent.modeReviewShield')
                                                        : s.landing_page_enabled
                                                            ? t('tapHealthMonitor.silent.modeLandingPage')
                                                            : s.interaction_mode}
                                            </td>
                                            <td className="px-3 py-2 text-right font-mono">
                                                {numberFmt(s.suppressed_in_window)}
                                            </td>
                                            <td className="px-3 py-2">
                                                {s.last_tap ? fmtLocalDateTime(s.last_tap) : '—'}
                                            </td>
                                            <td className="px-3 py-2">
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => {
                                                        setCardQuery(s.card_id);
                                                        setTimeout(() => runCardDiagnostic(), 0);
                                                    }}
                                                    data-testid={`silent-diagnose-${s.card_id}`}
                                                >
                                                    {t('tapHealthMonitor.silent.diagnose')}
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>
            </section>

            {/* ---------------- Write failures ---------------- */}
            <section>
                <h2 className="text-sm uppercase tracking-wide text-slate-500 mb-2">
                    {t('tapHealthMonitor.writeFailures.sectionLabel')}
                </h2>
                <Card className="p-4" data-testid="write-failures-card">
                    <div className="text-3xl font-semibold text-slate-900">
                        {numberFmt(failures?.total)}
                    </div>
                    {failures?.total ? (
                        <div className="mt-3 space-y-1 text-sm">
                            {Object.entries(failures.by_error_type).map(([k, v]) => (
                                <div key={k} className="flex justify-between">
                                    <span>{k}</span>
                                    <span className="font-mono">{numberFmt(v)}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-sm text-slate-500 mt-2">
                            {t('tapHealthMonitor.writeFailures.empty')}
                        </div>
                    )}
                </Card>
            </section>

            {/* ---------------- Per-card diagnostics ---------------- */}
            <section>
                <h2 className="text-sm uppercase tracking-wide text-slate-500 mb-2">
                    {t('tapHealthMonitor.diagnostic.sectionLabel')}
                </h2>
                <Card className="p-4" data-testid="card-diagnostic-card">
                    <form onSubmit={runCardDiagnostic} className="flex gap-2">
                        <Input
                            value={cardQuery}
                            onChange={(e) => setCardQuery(e.target.value)}
                            placeholder={t('tapHealthMonitor.diagnostic.placeholder')}
                            data-testid="card-diagnostic-input"
                        />
                        <Button
                            type="submit"
                            disabled={cardLoading || !cardQuery.trim()}
                            data-testid="card-diagnostic-submit-btn"
                        >
                            <Search className="h-4 w-4 mr-2" />
                            {cardLoading
                                ? t('tapHealthMonitor.diagnostic.running')
                                : t('tapHealthMonitor.diagnostic.run')}
                        </Button>
                    </form>
                    {cardError ? (
                        <div className="text-sm text-rose-600 mt-3" data-testid="card-diagnostic-error">
                            {t('tapHealthMonitor.diagnostic.error', { error: cardError })}
                        </div>
                    ) : null}
                    {cardResult ? (
                        <div className="mt-4 space-y-3" data-testid="card-diagnostic-result">
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <Badge className={
                                    cardResult.verdict === 'healthy'
                                        ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                                        : 'bg-rose-100 text-rose-700 border-rose-200'
                                }>
                                    {t(`tapHealthMonitor.verdict.${verdictKey(cardResult.verdict)}`, cardResult.verdict)}
                                </Badge>
                                <span className="font-mono text-sm break-all">{cardResult.card_id}</span>
                                <span className="text-sm text-slate-500 break-words">
                                    {cardResult.organization?.name}
                                </span>
                            </div>
                            {cardResult.verdict_reasons.length ? (
                                <ul className="list-disc list-inside text-sm text-slate-700">
                                    {cardResult.verdict_reasons.map((r, i) => (
                                        <li key={i}>{r}</li>
                                    ))}
                                </ul>
                            ) : null}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                                <div data-testid="card-diagnostic-tap-protection">
                                    <div className="text-slate-500 text-xs uppercase mb-1">
                                        {t('tapHealthMonitor.diagnostic.tapProtection')}
                                    </div>
                                    <TapProtectionSummary
                                        view={cardResult.tap_protection}
                                        t={t}
                                    />
                                </div>
                                <div>
                                    <div className="text-slate-500 text-xs uppercase">{t('tapHealthMonitor.diagnostic.taps')}</div>
                                    <ul className="text-slate-700">
                                        <li>24h: {numberFmt(cardResult.taps_by_window.last_24h)}</li>
                                        <li>7d: {numberFmt(cardResult.taps_by_window.last_7d)}</li>
                                        <li>30d: {numberFmt(cardResult.taps_by_window.last_30d)}</li>
                                    </ul>
                                </div>
                                <div>
                                    <div className="text-slate-500 text-xs uppercase">{t('tapHealthMonitor.diagnostic.suppressed')}</div>
                                    <ul className="text-slate-700">
                                        <li>24h: {numberFmt(cardResult.suppressed_by_window.last_24h)}</li>
                                        <li>7d: {numberFmt(cardResult.suppressed_by_window.last_7d)}</li>
                                        <li>30d: {numberFmt(cardResult.suppressed_by_window.last_30d)}</li>
                                    </ul>
                                </div>
                            </div>
                            {cardEvents?.summary ? (
                                <CardEventsSummary summary={cardEvents.summary} t={t} />
                            ) : null}
                            {cardEvents?.events?.length ? (
                                <EventStreamTable events={cardEvents.events} t={t} />
                            ) : cardResult.recent_suppressions?.length ? (
                                <SuppressionsTable
                                    rows={cardResult.recent_suppressions}
                                    t={t}
                                />
                            ) : null}
                        </div>
                    ) : null}
                </Card>
            </section>

            {/* ---------------- Footer meta ---------------- */}
            <div className="text-xs text-slate-400 flex items-center gap-2">
                <Activity className="h-3 w-3" />
                {t('tapHealthMonitor.footer', {
                    ts: summary?.generated_at
                        ? fmtLocalDateTime(summary.generated_at)
                        : '—',
                })}
            </div>
        </div>
    );
};

export default TapHealthMonitor;
